import React, { memo, useCallback, useState } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Sheet, SheetContent, SheetTrigger } from 'components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Card, CardContent } from 'components/ui/card';
import { IconSearch } from '@app/icons';
import {
  Kato,
  Oked,
  Tender,
  TenderStatus,
  TenderStatusNames,
} from '@app/models';
import { RenderFilterProps } from '@app/templates';
import { useForm } from '@app/forms';
import { useUserContext } from '@app/providers';
import ContactRequestsModal from 'modals/ContactRequests/ContactRequests';
import { KatoSelectTrigger } from '@app/components';
import { OkedSelectTrigger } from '@app/components';
import { SelectOption } from '@app/components';

interface FormData {
  name: string;
  kato: SelectOption<Kato> | null;
  oked: SelectOption<Oked> | null;
  status: SelectOption<TenderStatus> | null;
}

interface Props extends RenderFilterProps<Partial<Tender>> {}

function TendersFilter(props: Props) {
  const { onSubmit, pending, okedParentNumber } = props;
  const { user } = useUserContext();
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { values, onChange, setValues } = useForm<FormData>({
    name: '',
    oked: null,
    kato: null,
    status: null,
  });

  const performSearch = useCallback(() => {
    const filterData: Partial<Tender> = {};

    if (!!values.name) {
      filterData.name = values.name;
    }

    if (!!values.oked) {
      filterData.okedCode = values.oked.value.code
        ? values.oked.value.code
        : String(values.oked.value.number);
    }

    if (!!values.kato) {
      filterData.katoCode = values.kato.value.code;
    }

    if (!!values.status) {
      filterData.status = values.status.value;
    }

    onSubmit(filterData);
  }, [onSubmit, values]);

  const onClickSearch = useCallback(() => {
    if (!user.authenticated) {
      setIsContactModalVisible(true);
    } else {
      performSearch();
    }
  }, [user.authenticated, performSearch]);

  const handleContactSubmit = useCallback(async () => {
    setIsContactModalVisible(false);
    performSearch();
  }, [performSearch]);

  const onClickClear = useCallback(() => {
    setValues({
      name: '',
      oked: null,
      kato: null,
      status: null,
    });

    onSubmit({});
  }, [onSubmit, setValues]);

  const onClickClearOked = useCallback(() => {
    setValues({
      oked: null,
    });
  }, [setValues]);

  const onClickClearKato = useCallback(() => {
    setValues({
      kato: null,
    });
  }, [setValues]);

  const statusOptions = [
    TenderStatus.NEW,
    TenderStatus.ACTIVE,
    TenderStatus.CLOSED,
  ].map((statusKey) => ({
    label: TenderStatusNames[statusKey as unknown as TenderStatus],
    value: statusKey.toString(),
  }));

  return (
    <>
      <Card className="w-full hidden md:block">
        <CardContent className="p-4 sm:p-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                НАИМЕНОВАНИЕ
              </Label>
              <Input
                id="name"
                placeholder="Введите наименование"
                value={values.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(e.target.value, 'name')
                }
                className="w-full"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full">
              <div className="space-y-2">
                <KatoSelectTrigger
                  label="РЕГИОН"
                  value={
                    (!!values.kato ? values.kato.value.nameRu : '') || undefined
                  }
                  placeholder="Выберите регион"
                  name="kato"
                  onChange={onChange}
                  onClickClear={onClickClearKato}
                />
              </div>

              <div className="space-y-2">
                <OkedSelectTrigger
                  label={
                    okedParentNumber === 60000001
                      ? 'СПЕЦИАЛИЗАЦИЯ'
                      : 'КАТЕГОРИЯ'
                  }
                  name="oked"
                  value={!!values.oked ? values.oked.value.nameRu : ''}
                  placeholder={
                    okedParentNumber === 60000001
                      ? 'Выберите специализацию'
                      : 'Выберите категорию'
                  }
                  onChange={onChange}
                  parentNumber={okedParentNumber}
                  onClickClear={onClickClearOked}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className="text-sm font-medium">
                  СТАТУС ОБЬЯВЛЕНИЯ
                </Label>
                <Select
                  value={values.status?.value?.toString() || ''}
                  onValueChange={(value: string) => {
                    const selectedOption = statusOptions.find(
                      (opt) => opt.value === value
                    );
                    onChange(selectedOption || null, 'status');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите статус объявления" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-3 pt-4 border-t w-full sm:flex-row sm:justify-between">
              <Button
                type="button"
                onClick={onClickClear}
                className="reset-btn w-full sm:w-auto"
              >
                Сбросить все
              </Button>

              <Button
                disabled={pending}
                onClick={onClickSearch}
                className="w-full sm:w-auto min-w-[120px]"
              >
                {pending ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                ) : (
                  <IconSearch className="h-4 w-4 mr-2" />
                )}
                Найти
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="md:hidden">
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full">
              <IconSearch className="h-4 w-4 mr-2" />
              Расширенный поиск
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name-mobile" className="text-sm font-medium">
                  НАИМЕНОВАНИЕ
                </Label>
                <Input
                  id="name-mobile"
                  placeholder="Введите наименование"
                  value={values.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange(e.target.value, 'name')
                  }
                  className="w-full"
                />
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <KatoSelectTrigger
                    label="РЕГИОН"
                    value={
                      (!!values.kato ? values.kato.value.nameRu : '') ||
                      undefined
                    }
                    placeholder="Выберите регион"
                    name="kato"
                    onChange={onChange}
                    onClickClear={onClickClearKato}
                  />
                </div>

                <div className="space-y-2">
                  <OkedSelectTrigger
                    label={
                      okedParentNumber === 60000001
                        ? 'СПЕЦИАЛИЗАЦИЯ'
                        : 'КАТЕГОРИЯ'
                    }
                    name="oked"
                    value={!!values.oked ? values.oked.value.nameRu : ''}
                    placeholder={
                      okedParentNumber === 60000001
                        ? 'Выберите специализацию'
                        : 'Выберите категорию'
                    }
                    onChange={onChange}
                    parentNumber={okedParentNumber}
                    onClickClear={onClickClearOked}
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="status-mobile"
                    className="text-sm font-medium"
                  >
                    СТАТУС ОБЬЯВЛЕНИЯ
                  </Label>
                  <Select
                    value={values.status?.value?.toString() || ''}
                    onValueChange={(value: string) => {
                      const selectedOption = statusOptions.find(
                        (opt) => opt.value === value
                      );
                      onChange(selectedOption || null, 'status');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите статус объявления" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-3 pt-4 border-t w-full">
                <Button
                  type="button"
                  onClick={onClickClear}
                  variant="outline"
                  className="w-full"
                >
                  Сбросить все
                </Button>

                <Button
                  disabled={pending}
                  onClick={() => {
                    onClickSearch();
                    setIsSheetOpen(false);
                  }}
                  className="w-full"
                >
                  {pending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  ) : (
                    <IconSearch className="h-4 w-4 mr-2" />
                  )}
                  Найти
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <ContactRequestsModal
        visible={isContactModalVisible}
        onClose={() => setIsContactModalVisible(false)}
        onSubmit={handleContactSubmit}
        okedCode={
          values.oked?.value.code || values.oked?.value.number?.toString()
        }
        katoCode={values.kato?.value.code}
      />
    </>
  );
}

export default memo(TendersFilter);
